// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { WizardStep } from '../types/wizard';
import type { WizardStepId } from '../types/wizard';
import { PoweredByFinerty } from './shared/PoweredByFinerty';
import './WizardSidebar.css';

interface WizardSidebarProps {
  steps: WizardStep[];
  currentStepId: WizardStepId;
  onStepClick: (stepId: WizardStepId) => void;
  canNavigateTo: (stepId: WizardStepId) => boolean;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export function WizardSidebar({
  steps,
  currentStepId,
  onStepClick,
  canNavigateTo,
  mobileOpen,
  onCloseMobile,
}: WizardSidebarProps) {
  const sidebarClassName = [
    'wizard-sidebar',
    mobileOpen ? 'wizard-sidebar--mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <nav
      className={`${sidebarClassName} bg-white border-r border-[#E1E1E1] overflow-y-auto`}
      style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
      aria-label="Form steps"
    >
      {mobileOpen && (
        <button
          className="wizard-sidebar__close absolute top-3 right-3 bg-transparent border-none text-grey-02 text-xl cursor-pointer p-2 hidden"
          onClick={onCloseMobile}
          aria-label="Close navigation"
          type="button"
        >
          ✕
        </button>
      )}
      <ol className="list-none m-0 p-0 flex flex-col gap-2 pt-6" role="list">
        {steps.map((step) => {
          const isActive = step.id === currentStepId;
          const isDisabled = !canNavigateTo(step.id);
          const isComplete = step.status === 'complete';
          const isError = step.status === 'error';

          const ariaLabel = isComplete
            ? `${step.name}, completed`
            : isError
              ? `${step.name}, has errors`
              : step.name;

          return (
            <li
              key={step.id}
              className={`flex items-center justify-between min-h-[48px] w-[260px] pr-lg py-[12px] gap-[19px] cursor-pointer transition-colors font-sans text-body-02 font-medium leading-normal not-italic
                ${isActive
                  ? 'bg-lilac-05 text-purple-primary rounded-r-[8px] border-l-[5px] border-purple-primary pl-[19px]'
                  : 'text-black hover:bg-[#F5F5F7] hover:rounded-r-[8px] pl-lg'}
                ${isDisabled ? 'opacity-45 cursor-not-allowed' : ''}
              `}
              role="listitem"
              aria-current={isActive ? 'step' : undefined}
              aria-disabled={isDisabled}
              aria-label={ariaLabel}
              tabIndex={isDisabled ? -1 : 0}
              onClick={() => {
                if (!isDisabled) {
                  onStepClick(step.id);
                  if (mobileOpen && onCloseMobile) {
                    onCloseMobile();
                  }
                }
              } }
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                  e.preventDefault();
                  onStepClick(step.id);
                  if (mobileOpen && onCloseMobile) {
                    onCloseMobile();
                  }
                }
              } }
            >
              <span>{step.name}</span>
              {isComplete && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
                  <mask id={`mask_${step.id}`} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                    <path d="M24 0H0V24H24V0Z" fill="white"/>
                  </mask>
                  <g mask={`url(#mask_${step.id})`}>
                    <path d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM16.78 9.7L11.11 15.37C10.97 15.51 10.78 15.59 10.58 15.59C10.38 15.59 10.19 15.51 10.05 15.37L7.22 12.54C6.93 12.25 6.93 11.77 7.22 11.48C7.51 11.19 7.99 11.19 8.28 11.48L10.58 13.78L15.72 8.64C16.01 8.35 16.49 8.35 16.78 8.64C17.07 8.93 17.07 9.4 16.78 9.7Z" fill="#00A63E"/>
                  </g>
                </svg>
              )}
              {isError && (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shrink-0"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2L1 21H23L12 2ZM12 9V14M12 17V18"
                    stroke="#E7000B"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="#FFEAEA"
                  />
                </svg>
              )}
            </li>
          );
        })}
      </ol>

      <div className="px-6 pb-6 flex justify-center">
        <PoweredByFinerty size="small" />
      </div>
    </nav>
  );
}
